/**
 * Factory for creating a localStorage-backed external store
 * compatible with React's useSyncExternalStore.
 *
 * Encapsulates the common pattern shared by useBookmarks,
 * useRecentEntries, and useSavedSearches.
 */
export function createLocalStorageStore<T>(
  storageKey: string,
  maxItems: number
) {
  let listeners: Array<() => void> = []
  const EMPTY: T[] = []
  let cachedSnapshot: T[] = EMPTY
  let cachedRaw: string | null | undefined = undefined

  function emitChange() {
    for (const listener of listeners) {
      listener()
    }
  }

  function subscribe(listener: () => void): () => void {
    listeners = [...listeners, listener]
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }

  function read(): T[] {
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  function getStableSnapshot(): T[] {
    const raw = localStorage.getItem(storageKey)
    if (raw !== cachedRaw) {
      cachedRaw = raw
      cachedSnapshot = raw ? read() : EMPTY
    }
    return cachedSnapshot
  }

  function write(items: T[]) {
    localStorage.setItem(storageKey, JSON.stringify(items.slice(0, maxItems)))
    cachedRaw = undefined
    emitChange()
  }

  function clear() {
    localStorage.removeItem(storageKey)
    cachedRaw = undefined
    emitChange()
  }

  return { subscribe, getStableSnapshot, read, write, clear }
}
